import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getRoleDashboard } from '@/components/feature/AuthGuard';
import { useSetting } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/#about' },
  { name: 'Tracks', path: '/#tracks' },
  { name: 'FAQ', path: '/#faq' },
  { name: 'Contact', path: '/#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const eventName = useSetting('event_name', eventInfo.name);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = location.pathname === '/';
  const isDashboard = location.pathname.startsWith('/dashboard');

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    if (path.startsWith('/#')) {
      const id = path.substring(2);
      if (isHome) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = '/' + path.substring(1);
      }
    }
  };

  const textColor = (scrolled || !isHome) ? 'text-foreground-900' : 'text-white';
  const mutedColor = (scrolled || !isHome) ? 'text-foreground-700' : 'text-white/80';
  const hoverBg = (scrolled || !isHome) ? 'hover:bg-background-100' : 'hover:bg-white/10';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome || isDashboard
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-heart-pulse-line text-background-50 text-lg md:text-xl"></i>
            </div>
            <span className={`font-heading font-bold text-lg md:text-xl whitespace-nowrap ${scrolled || !isHome || isDashboard ? 'text-foreground-900' : 'text-white'}`}>
              {eventName}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                onClick={(e) => {
                  if (link.path.startsWith('/#')) {
                    e.preventDefault();
                    handleNavClick(link.path);
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${mutedColor} ${hoverBg} hover:${textColor}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user && profile ? (
              <>
                <Link
                  to={getRoleDashboard(profile.role)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled || !isHome || isDashboard
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <i className="ri-dashboard-line mr-1.5"></i>
                  Dashboard
                </Link>
                <button
                  onClick={signOut}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled || !isHome || isDashboard
                      ? 'text-foreground-600 hover:text-red-600 border border-background-300 hover:border-red-300'
                      : 'text-white/80 hover:text-white border border-white/30 hover:border-white/50'
                  }`}
                >
                  <i className="ri-logout-box-line mr-1.5"></i>
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-950 border border-background-300 hover:border-background-400'
                      : 'text-white/90 hover:text-white border border-white/30 hover:border-white/50'
                  }`}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Register Now
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${scrolled || !isHome || isDashboard ? 'text-foreground-900' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/60 bg-background-50 rounded-b-2xl">
            <div className="flex flex-col gap-1 pt-3">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.path}
                  onClick={(e) => {
                    if (link.path.startsWith('/#')) {
                      e.preventDefault();
                      handleNavClick(link.path);
                    }
                  }}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  {link.name}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-3 px-4">
                {user && profile ? (
                  <>
                    <Link
                      to={getRoleDashboard(profile.role)}
                      onClick={() => setMobileOpen(false)}
                      className="w-full py-2.5 rounded-md text-center text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-dashboard-line mr-1.5"></i> Dashboard
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); signOut(); }}
                      className="w-full py-2.5 rounded-md text-center text-sm font-medium text-red-600 border border-red-300 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line mr-1.5"></i> Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="w-full py-2.5 rounded-md text-center text-sm font-medium text-foreground-700 border border-background-300 hover:border-background-400 transition-colors cursor-pointer"
                      onClick={() => setMobileOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="w-full py-2.5 rounded-md text-center text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 transition-colors cursor-pointer"
                      onClick={() => setMobileOpen(false)}
                    >
                      Register Now
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}